import type { FunctionComponentWithRef, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { postMagazineLeadContentItemContent$key } from "scmp-app/queries/__generated__/postMagazineLeadContentItemContent.graphql";

import {
  AdSlot,
  Container,
  ContentContainer,
  CoverImage,
  Headline,
  StyledContentEntityLink,
  StyledCoverEntityLink,
  Summary,
  Topic,
  Wrapper,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  children?: ReactNode;
  reference: postMagazineLeadContentItemContent$key;
};

export const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  children,
  className,
  onClick,
  ref,
  reference,
  topicLinkVariant,
}) => {
  const content = useFragment(
    graphql`
      fragment postMagazineLeadContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withCoverImage: true
            withCoverImageLandscape250x99: true
            withCoverImageSize768x768: true
            withHeadline: true
            withTopicLink: true
            withSummary: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content, topicLinkVariant);
  const topic = providers.topicLink?.({ preferShortName: true });
  const headline = providers.headline({ hideTooltip: true, preferSocialHeadline: true });
  const summary = providers.summary();

  return (
    <Container className={className} ref={ref}>
      <StyledCoverEntityLink
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        reference={content}
      >
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "landscape250x99",
              mobileUp: "size768x768",
              tabletUp: "landscape250x99",
            },
          })}
        </CoverImage>
      </StyledCoverEntityLink>
      <Wrapper>
        <ContentContainer>
          <Topic>{topic}</Topic>
          <StyledContentEntityLink
            onClick={() => onClick?.(content.entityId, content.urlAlias)}
            reference={content}
          >
            <Headline>{headline}</Headline>
            <Summary>{summary}</Summary>
          </StyledContentEntityLink>
        </ContentContainer>

        <AdSlot>{children}</AdSlot>
      </Wrapper>
    </Container>
  );
};

Component.displayName = "ContentItemMagazinesPostMagLead";

export const ContentItemMagazinesPostMagLead = Component;
